import { useMemo, useState } from "react";
import { Landmark } from "lucide-react";
import { ToolLayout, TwoCol } from "@/components/tools/ToolLayout";
import { SectionCard, Field, MoneyInput, SliderRow, StatCard, InfoRow, compactINR } from "@/components/tools/kit";
import { GrowthArea, SplitDonut } from "@/components/tools/charts";
import { nps } from "@/lib/calculators";

export default function Nps() {
  const [monthly, setMonthly] = useState(10000);
  const [rate, setRate] = useState(10);
  const [currentAge, setAge] = useState(30);
  const [annuityPct, setAnnuity] = useState(40);
  const [annuityReturn, setAnnuityReturn] = useState(6);
  const r = useMemo(() => nps({ monthly, rate, currentAge, annuityPct, annuityReturn }), [monthly, rate, currentAge, annuityPct, annuityReturn]);
  return (
    <ToolLayout title="NPS Calculator" subtitle="Pension corpus at 60 and your monthly payout" icon={Landmark} accent="#2D6A4F">
      <TwoCol
        left={
          <SectionCard title="Your NPS">
            <div className="space-y-5">
              <Field label="Monthly contribution"><MoneyInput testid="nps-monthly" value={monthly} onChange={setMonthly} helper={compactINR(monthly) + " / month"} /></Field>
              <Field label="Current age" hint={currentAge}><SliderRow testid="nps-age" value={currentAge} min={18} max={59} suffix=" yr" onChange={setAge} /></Field>
              <Field label="Expected return (p.a.)" hint={rate + "%"}><SliderRow testid="nps-rate" value={rate} min={5} max={15} step={0.5} suffix="%" onChange={setRate} /></Field>
              <Field label="Annuity portion at 60" hint={annuityPct + "%"}><SliderRow testid="nps-annuity" value={annuityPct} min={40} max={100} suffix="%" onChange={setAnnuity} /></Field>
              <Field label="Annuity return (p.a.)" hint={annuityReturn + "%"}><SliderRow testid="nps-annuity-return" value={annuityReturn} min={4} max={9} step={0.5} suffix="%" onChange={setAnnuityReturn} /></Field>
              <p className="text-xs text-[#8A9790]">At 60, at least 40% must buy an annuity; the rest can be withdrawn tax-free.</p>
            </div>
          </SectionCard>
        }
        right={
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <StatCard testid="nps-corpus" label="Corpus at 60" value={compactINR(r.corpus)} accent="#2D6A4F" big />
              <StatCard testid="nps-lumpsum" label="Lump sum withdrawal" value={compactINR(r.lumpSum)} accent="#D99B26" />
              <StatCard testid="nps-pension" label="Monthly pension" value={compactINR(r.monthlyPension)} accent="#2A6F7F" />
            </div>
            <SectionCard title="Corpus growth"><GrowthArea data={r.points} keys={[{ key: "value", name: "Corpus", color: "#2D6A4F" }, { key: "invested", name: "Invested", color: "#D99B26" }]} /></SectionCard>
            <SectionCard title="At retirement (age 60)">
              <SplitDonut segments={[{ name: "Lump sum (tax-free)", value: Math.round(r.lumpSum), color: "#D99B26" }, { name: "Annuity corpus", value: Math.round(r.annuityCorpus), color: "#2A6F7F" }]} />
              <InfoRow label="Invested over the years" value={compactINR(r.invested)} />
              <InfoRow label="Wealth gained" value={compactINR(r.gains)} strong />
            </SectionCard>
          </>
        }
      />
    </ToolLayout>
  );
}
